import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DocumentPopup from "../../components/Reusable/DocumentPopup";
import { truncateText } from "../../utils/truncate";
import { useJobStore } from "../../store/useJobStore";
import formatDate from "./../../components/Reusable/dateUtils";
import { Trash2 } from "lucide-react";
import ConfirmationPopup from "../../components/Reusable/ConfirmationPopup";
import { FaRegNoteSticky } from "react-icons/fa6";

const PublishJob = () => {
  const [showDocumentPopup, setShowDocumentPopup] = useState(false);
  const [documentUrl, setDocumentUrl] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteJobPopup, setDeleteJobPopup] = useState(false);
  const [jobId, setJobId] = useState("");
  const [jobName, setJobName] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const navigate = useNavigate();
  const permissions = JSON.parse(localStorage.getItem("userPermissions"));
  const hasPostJobPermission = permissions.some(
    (permission) => permission.name === "view_per_group_all_employees_info"
  );
  const { jobPositions, fetchJobPositions } = useJobStore();

  useEffect(() => {
    fetchJobPositions();
  }, []);

  // console.log("jobPositions", jobPositions);

  const handleOpenDocumentPopup = (url) => {
    setDocumentUrl(url);
    setShowDocumentPopup(true);
  };

  const handleDeleteJob = (id, name) => {
    setJobId(id);
    setJobName(name);
    setDeleteJobPopup(true);
  };

  const deleteJobRecord = async () => {
    const accessToken = localStorage.getItem("accessToken");
    const apiBaseUrl = process.env.REACT_APP_BASE_URL;

    try {
      const myHeaders = new Headers();
      myHeaders.append("Authorization", `Bearer ${accessToken}`);

      const formData = new FormData();

      const requestOptions = {
        method: "DELETE",
        headers: myHeaders,
        body: formData,
        redirect: "follow",
      };

      const res = await fetch(
        `${apiBaseUrl}/job-positions/${jobId}`,
        requestOptions
      );

      if (!res.ok) {
        const err = await res.text();
        console.error(err);
        return;
      }

      const result = await res.text();
      // console.log(result);

      fetchJobPositions();
      setDeleteJobPopup(false);
    } catch (error) {
      console.error("Error submitting:", error);
    }
  };

  // Filter jobs based on selected month and year
  const filteredJobs = jobPositions?.filter((job) => {
    if (!selectedMonth && !selectedYear) return true;

    const jobDate = new Date(job.posting_date);
    const jobMonth = jobDate.getMonth() + 1;
    const jobYear = jobDate.getFullYear();

    const monthMatch = !selectedMonth || jobMonth === parseInt(selectedMonth);
    const yearMatch = !selectedYear || jobYear === parseInt(selectedYear);

    return monthMatch && yearMatch;
  });

  const totalPages = Math.ceil(filteredJobs?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = filteredJobs?.slice(startIndex, endIndex);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goToPreviousPage = () => {
    setCurrentPage((prevPage) => prevPage - 1);
  };

  const goToNextPage = () => {
    setCurrentPage((prevPage) => prevPage + 1);
  };

  const cancelPopup = () => {
    setDeleteJobPopup(false);
  };

  const handleMonthChange = (e) => {
    setSelectedMonth(e.target.value);
    setCurrentPage(1);
  };

  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
    setCurrentPage(1);
  };

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

  const months = [
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  return (
    <div className="board__layout">
      <div className="section-header">
        <h1>Current Job Openings</h1>
        <div className="flex gap-3 items-center">
          <select
            value={selectedMonth}
            onChange={handleMonthChange}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Months</option>
            {months.map((month) => (
              <option key={month.value} value={month.value}>
                {month.label}
              </option>
            ))}
          </select>
          <select
            value={selectedYear}
            onChange={handleYearChange}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Years</option>
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
        {hasPostJobPermission && (
          <button
            className="resignation-btn"
            onClick={() => navigate("/post-newjob")}
          >
            + New Job
          </button>
        )}
      </div>

      <div className="flex flex-col gap-4">
        {currentData.length > 0 ? (
          (currentData || [])
            .sort((a, b) => new Date(b.posting_date) - new Date(a.posting_date))
            .map((job) => (
              <div
                key={job?.id}
                className="grid grid-cols-[1.5fr_3fr_4fr_1fr_1fr] items-center  bg-white rounded-lg p-6"
              >
                <div>
                  <label className="text-lg font-normal text-medium-dark-gray">
                    Date
                  </label>
                  <p className="text-lg font-normal text-desc-gray">
                    {formatDate(job?.posting_date)}
                  </p>
                </div>

                <div>
                  <label className="text-lg font-normal text-medium-dark-gray">
                    Job Position
                  </label>
                  <p className="text-lg font-normal text-desc-gray">
                    {truncateText(job?.name)}
                  </p>
                </div>

                <div>
                  <label className="text-lg font-normal text-medium-dark-gray">
                    Company Name
                  </label>
                  <p className="text-lg font-normal text-desc-gray">
                    {truncateText(job?.company?.name)}
                  </p>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={() => handleOpenDocumentPopup(job?.image_url)}
                    className="flex items-center justify-center w-[108px] h-12 text-medium-dark-gray font-semibold border border-[#b7b7b7] rounded-[10px] hover:text-medium-dark-gray/90"
                  >
                    View
                  </button>
                </div>
                {hasPostJobPermission && (
                  <div
                    onClick={() => handleDeleteJob(job?.id, job?.name)}
                    className="flex justify-end cursor-pointer"
                  >
                    <Trash2 className="text-red-600" />
                  </div>
                )}
              </div>
            ))
        ) : (
          <div className="flex flex-col items-center justify-center text-center py-24 text-gray-500 bg-white text-2xl">
            <FaRegNoteSticky className="text-6xl" />
            No job positions found for the selected period.
          </div>
        )}
      </div>

      {filteredJobs?.length > 0 && (
        <div className="pagination">
          <div>
            <button onClick={goToPreviousPage} disabled={currentPage === 1}>
              <i className="fa-solid fa-arrow-left-long"></i> Previous
            </button>
          </div>
          <div>
            {Array.from({ length: totalPages }, (_, index) => {
              const startPage = currentPage - 5 > 0 ? currentPage - 5 : 1;
              const endPage =
                startPage + 9 < totalPages ? startPage + 9 : totalPages;
              return (
                index + 1 >= startPage &&
                index + 1 <= endPage && (
                  <button
                    key={index}
                    onClick={() => handlePageChange(index + 1)}
                    className={currentPage === index + 1 ? "active" : ""}
                  >
                    {index + 1}
                  </button>
                )
              );
            })}
          </div>
          <div>
            <button onClick={goToNextPage} disabled={currentPage >= totalPages}>
              Next <i className="fa-solid fa-arrow-right-long"></i>
            </button>
          </div>
          
        </div>
      )}
      {showDocumentPopup && (
        <DocumentPopup
          documentUrl={documentUrl}
          onClose={() => setShowDocumentPopup(false)}
        />
      )}
      {deleteJobPopup && (
        <ConfirmationPopup
          confirm={deleteJobRecord}
          cancel={cancelPopup}
          title="Delete Job"
          question={`Do you want to delete the ${jobName} Job`}
        />
      )}
    </div>
  );
};

export default PublishJob;
